import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import noop from 'lodash/noop';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import { Flyout } from '@els/els-react--flyout';
import { LinkElement } from '@els/els-react--link-element';
import { ButtonWithIcon } from '@els/els-react--button';
import UserDatabases from '@Services/UserDatabases';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import ReactionCitations from '@Components/Results/reactions/reaction-details/ReactionCitations';
import Catalyst from '@Services/Catalyst';

import './ConditionsReferenceExperiment.scss';

const ConditionsReferenceExperiment = function ConditionsReferenceExperimentComponent({
  metadata,
  detail,
  index,
  onFullTextLinkClick = () => null,
  onIssueCitedCounterClick = () => null,
  onAbstractOpen = () => null,
  onAuthorClick = () => null,
}) {
  const { citations, elnCitation, experiment, producer, project, created, modified, fullTextLinkEntities, scientist } = detail;

  const scientistHtml = useMemo(() => {
    if (!scientist) return '';
    return isArray(scientist) ? `${scientist.join('; ')};` : `${[].join('; ')};`;
  }, [scientist]);

  const experimentalDataSheetLinkClick = useCallback(
    (fullText) => {
      const dbName = UserDatabases.getDbName(get(metadata, 'databaseId'));
      const experimentId = get(detail, 'experiment');
      Catalyst.trackLinkOut(
        { id: `linkout:${experimentId}`, type: `${Catalyst.getPrefix()}:linkout:${dbName}:experimental data sheet` },
        { location: fullText.link, name: fullText.type }
      );
    },
    [metadata, detail]
  );

  const experimentalDataSheetList = useMemo(() => {
    if (!isArray(fullTextLinkEntities) || isEmpty(fullTextLinkEntities)) return null;
    return (
      <ul>
        {fullTextLinkEntities.map((fullText) => (
          <li key={fullText.link}>
            <LinkElement
              data-e2e="experimental-data-sheet-link"
              href={fullText.link}
              isExternal
              className="u-els-padding-top-1o2 u-els-padding-bottom-1o2"
              onClick={() => experimentalDataSheetLinkClick(fullText)}>
              {fullText.type}
            </LinkElement>
          </li>
        ))}
      </ul>
    );
  }, [fullTextLinkEntities, experimentalDataSheetLinkClick]);

  return (
    <>
      {(!isEmpty(citations) || elnCitation) && (
        <div className="references-block" data-testid="references-block">
          {citations &&
            citations.map((citation, citationIndex) => (
              <ReactionCitations
                metadata={metadata}
                // eslint-disable-next-line react/no-array-index-key
                key={citationIndex}
                index={index}
                citation={citation}
                onFullTextLinkClick={onFullTextLinkClick}
                onIssueCitedCounterClick={onIssueCitedCounterClick}
                onAbstractOpen={onAbstractOpen}
                onAuthorClick={onAuthorClick}
              />
            ))}
          {elnCitation && <InnerHTML tag="div" html={elnCitation} extendedTags />}
        </div>
      )}
      {!isEmpty(experiment) && (
        <div className="ci-reaction-details" data-testid="ci-reaction-details">
          <span className="ci-details-header u-els-display-block u-els-margin-top">Experiment</span>
          <div className="ci-details-group u-els-margin-top">
            {!isEmpty(producer) && (
              <div>
                <b>Producer: </b> <InnerHTML tag="span" html={producer} extendedTags />
              </div>
            )}
            <div>
              <b>Scientist: </b> <InnerHTML tag="span" html={scientistHtml} extendedTags />
            </div>
            <div>
              <b>Experiment ID: </b> <InnerHTML tag="span" html={experiment} extendedTags />
            </div>
            <div>
              <b>Project: </b> <InnerHTML tag="span" html={project} extendedTags />
            </div>
            <div>
              <b>Created: </b> <InnerHTML tag="span" html={created} extendedTags />
            </div>
            <div>
              <b>Modified: </b> <InnerHTML tag="span" html={modified} extendedTags />
            </div>
          </div>
          {isArray(fullTextLinkEntities) && !isEmpty(fullTextLinkEntities) && (
            <Flyout
              className="stage-experimental-procedure"
              flyout={
                <div className="u-els-font-size-base u-els-color-text u-els-margin-right-2x">
                  <div className="u-els-margin-bottom-1o2 u-els-font-size-h4 u-els-nowrap">Available through</div>
                  {experimentalDataSheetList}
                </div>
              }
              shouldCloseOnESC
              showCloseButton
              placement="bottom-end"
              pointerType="primary">
              <ButtonWithIcon
                data-e2e="available-through"
                sprite="ChevronDown"
                type="link"
                linkIconSize="xs"
                iconRight
                className="e2e-available-through"
                onClick={noop}>
                Experimental Data Sheet
              </ButtonWithIcon>
            </Flyout>
          )}
        </div>
      )}
    </>
  );
};

ConditionsReferenceExperiment.propTypes = {
  metadata: MetadataPropTypesShape,
  detail: PropTypes.object.isRequired,
  index: PropTypes.string.isRequired,
  onFullTextLinkClick: PropTypes.func,
  onIssueCitedCounterClick: PropTypes.func,
  onAbstractOpen: PropTypes.func,
  onAuthorClick: PropTypes.func,
};

export default React.memo(ConditionsReferenceExperiment);
